
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, X, Wifi, WifiOff, Home, Plus } from 'lucide-react';
import { BrowserInstructions } from './BrowserInstructions';
import { useFirstLaunch } from '@/hooks/useFirstLaunch';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';
import { isNativeApp } from '@/lib/capacitor';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function MobilePWAPrompt() {
  const _isNative = isNativeApp();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  
  const { isFirstLaunch, isMobile, markAsReturningUser, isPWAInstalled } = useFirstLaunch();

  // Fonction pour vérifier si la popup a été fermée aujourd'hui
  const isDismissedToday = () => {
    try {
      const lastDismissed = localStorage.getItem('pwa-last-dismissed');
      if (!lastDismissed) return false;
      
      const today = new Date().toDateString();
      const dismissedDate = new Date(lastDismissed).toDateString();
      
      return today === dismissedDate;
    } catch (error) {
      logger.error('Error checking daily dismissal:', toError(error).message);
      return false;
    }
  };

  // Fonction pour marquer comme fermé aujourd'hui
  const markDismissedToday = () => {
    try {
      localStorage.setItem('pwa-last-dismissed', new Date().toISOString());
    } catch (error) {
      logger.error('Error marking daily dismissal:', toError(error).message);
    }
  };

  // Écouter l'événement beforeinstallprompt (Chrome/Edge uniquement)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      logger.debug('PWA: beforeinstallprompt event received');
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Écouter l'événement personnalisé pour ouvrir le modal
    const handlePWAInstallRequested = () => {
      logger.debug('PWA: Install requested via custom event');
      setShowModal(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-install-requested', handlePWAInstallRequested);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-install-requested', handlePWAInstallRequested);
    };
  }, []);

  // Logique d'affichage avec vérification du dismissal quotidien
  useEffect(() => {
    logger.debug('PWA Prompt Logic:', {
      isMobile,
      isPWAInstalled: isPWAInstalled(),
      isFirstLaunch,
      currentPath: window.location.pathname,
      permanentlyDismissed: localStorage.getItem('pwa-permanently-dismissed'),
      dismissedToday: isDismissedToday()
    });

    // Ne pas afficher si pas mobile
    if (!isMobile) {
      logger.debug('PWA: Not mobile, hiding');
      return;
    }

    // Ne pas afficher si PWA déjà installée
    if (isPWAInstalled()) {
      logger.debug('PWA: Already installed, hiding');
      return;
    }

    // Ne pas afficher si définitivement rejeté
    const permanentlyDismissed = localStorage.getItem('pwa-permanently-dismissed');
    if (permanentlyDismissed) {
      logger.debug('PWA: Permanently dismissed, hiding');
      return;
    }

    // Ne pas afficher si fermé aujourd'hui
    if (isDismissedToday()) {
      logger.debug('PWA: Dismissed today, hiding until tomorrow');
      return;
    }

    // Afficher le modal pour les nouveaux utilisateurs après un court délai
    if (isFirstLaunch) {
      logger.debug('PWA: First launch, showing modal after delay');
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 2000); // 2 secondes
      return () => clearTimeout(timer);
    }

    // Afficher le bouton flottant pour les utilisateurs récurrents après un délai plus long
    logger.debug('PWA: Returning user, showing floating button after delay');
    const timer = setTimeout(() => {
      setShowFloatingButton(true);
    }, 5000); // 5 secondes pour être moins intrusif
    return () => clearTimeout(timer);
  }, [isMobile, isFirstLaunch, isPWAInstalled]);

  const handleInstallClick = async () => {
    logger.debug('PWA: Install button clicked', { deferredPrompt: !!deferredPrompt });
    
    if (deferredPrompt) {
      // Chrome/Edge - utiliser l'API native
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        logger.info('PWA: Install outcome:', outcome);
        
        if (outcome === 'accepted') {
          localStorage.setItem('pwa-installed', 'true');
          markAsReturningUser();
          hideAllPrompts();
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        logger.error('PWA: Error during installation:', toError(error).message);
        // Fallback vers les instructions
        setShowModal(true);
      }
    } else {
      // Safari/autres - afficher les instructions
      logger.debug('PWA: No deferredPrompt, showing instructions');
      setShowModal(true);
    }
  };

  const handlePermanentDismiss = () => {
    logger.debug('PWA: Permanently dismissed');
    localStorage.setItem('pwa-permanently-dismissed', new Date().toISOString());
    markAsReturningUser();
    hideAllPrompts();
  };

  const handleTemporaryDismiss = () => {
    logger.debug('PWA: Temporarily dismissed for today');
    markDismissedToday();
    hideAllPrompts();
  };

  const hideAllPrompts = () => {
    setShowModal(false);
    setShowFloatingButton(false);
  };

  // Ne pas afficher si pas mobile ou PWA installée
  if (_isNative || !isMobile || isPWAInstalled()) {
    return null;
  }

  // Modal pour nouveaux utilisateurs
  if (showModal) {
    return (
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md mx-auto my-8 max-h-[80vh] overflow-y-auto">
          <DialogHeader className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
              <Home className="h-8 w-8 text-primary" />
            </div>
            <DialogTitle className="text-xl">
              Installer Sclepios sur votre téléphone
            </DialogTitle>
            <DialogDescription className="text-center space-y-2">
              <p>Accès instantané depuis votre écran d'accueil !</p>
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mt-4">
                <div className="flex items-center gap-1">
                  <Wifi className="h-4 w-4 text-green-500" />
                  <span>En ligne</span>
                </div>
                <div className="flex items-center gap-1">
                  <WifiOff className="h-4 w-4 text-orange-500" />
                  <span>Hors-ligne</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {deferredPrompt ? (
              <Button
                onClick={handleInstallClick}
                className="w-full h-12 text-base bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              >
                <Download className="mr-2 h-5 w-5" />
                Installer maintenant
              </Button>
            ) : (
              <BrowserInstructions />
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleTemporaryDismiss}
                className="flex-1"
              >
                Plus tard
              </Button>
              <Button
                variant="ghost"
                onClick={handlePermanentDismiss}
                className="flex-1 text-muted-foreground"
              >
                Ne plus proposer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Bouton flottant pour utilisateurs récurrents (moins intrusif maintenant)
  if (showFloatingButton) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Card className="w-64 sm:w-72 shadow-lg border border-primary/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus className="h-3 w-3 text-primary" />
                <CardTitle className="text-xs font-medium">
                  Installer l'app
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTemporaryDismiss}
                className="h-5 w-5 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-3 space-y-2">
            <Button
              onClick={handleInstallClick}
              className="w-full text-xs h-8"
              size="sm"
            >
              <Download className="mr-1 h-3 w-3" />
              Installer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
